// Limits — the ceilings on what an author types (safety first, proposed
// QA-22; the handoff names them CL-5 and CL-6). One home, so every BC that
// checks a length checks the same number. Proposed defaults: the owner
// sets the numbers.
//
// An honest author can reach these (a long paste), so each is refused at
// decide time with a NAMED error (`*_too_long`, carrying the ceiling), and
// the client stops typing at the same number. A list only a crafted request
// can overfill (a project's audiences) is capped at the contract instead.
export { AUDIENCES_MAX, BLOCK_TEXT_MAX, CHAT_MESSAGE_MAX, CONVERSATION_INSTRUCTIONS_CHARS_MAX, CONVERSATION_INSTRUCTIONS_MAX, DECLINED_INSTRUCTIONS_REMEMBERED, FEEDBACK_REPORTS_PER_DAY_MAX, INSTRUCTION_PROPOSAL_REASON_MAX, INSTRUCTION_PROPOSALS_OPEN_MAX, INSTRUCTION_PROPOSALS_PER_TURN_MAX, INSTRUCTION_TEXT_MAX, LANGUAGE_PROFILES_PER_CREATE_MAX, LONG_TEXT_MAX, NEW_PROJECT_MODULES_MAX, PAGE_BLOCKS_MAX, PLANNED_PARTS_MAX, PROJECT_CONVERSATIONS_MAX, PROJECT_INSTRUCTIONS_CHARS_MAX, PROJECT_INSTRUCTIONS_MAX, PROJECT_OBJECTIVES_MAX, QUESTION_ANSWERS_MAX, SEARCH_QUERY_MAX, SEARCH_RESULTS_MAX, SHORT_TEXT_MAX, SOURCES_PER_CREATE_MAX, STRUCTURE_PARTS_MAX, TURN_ATTEMPTS_MAX, TURN_MINUTES_MAX, WAITING_MESSAGES_MAX, WORKSPACE_INSTRUCTIONS_CHARS_MAX, WORKSPACE_INSTRUCTIONS_MAX, };
// CL-5: one-line text — a project's name, an audience's label.
const SHORT_TEXT_MAX = 200;
// CL-6: long text — what a project is about.
const LONG_TEXT_MAX = 5_000;
// {block}: one block's text — a paragraph, heading or question written on a
// page. Capped at the contract (a manual edit); a longer paste goes in as a
// source through the file door instead.
const BLOCK_TEXT_MAX = 10_000;
// CL-20 (proposed): the learning objectives one project (its material)
// holds. The coverage page draws a column for each; an honest author can
// reach it, so one more is refused by name (`project_objectives_full`,
// carrying the ceiling).
const PROJECT_OBJECTIVES_MAX = 100;
// (proposed, #404) {conversations}: the conversations one project holds, not
// counting deleted ones (archived ones count). The Studio's picker lists them
// all; an honest author can reach it, so one more is refused by name
// (`project_conversations_full`, carrying the ceiling). Deleting one makes
// room.
const PROJECT_CONVERSATIONS_MAX = 200;
// CL-22 (proposed, QA-30) {modules}: the modules the New project box makes with a
// new project. The box stops offering "Add a module" (and stops guessing)
// at it; only a crafted request can send more, so the contract caps it.
// More modules come later, in the outline.
const NEW_PROJECT_MODULES_MAX = 20;
// CL-24 (proposed, QA-33) {blocks}: the blocks one page holds (a course,
// module or document page), its parts and a question's answers counted
// alike: every node of its content tree. Paragraphs, headings, questions,
// answers and parts all count. An edit that would take a page past it is
// refused by name (`page_blocks_full`, carrying the ceiling); one that
// keeps or lowers the count always lands, so a page made bigger before the
// ceiling can still be trimmed. Bounds a burst of empty lines.
const PAGE_BLOCKS_MAX = 1_000;
// CL-25 (proposed, QA-34) {answers}: the answers one question holds (its
// options). The number is the quiz template's own (2..6 options, A to F).
// An edit that would give a question one more is refused by name
// (`question_answers_full`, carrying the ceiling); one that keeps or lowers
// a question's count always lands. At the ceiling the editor shows the
// Limit hint instead of starting another answer.
const QUESTION_ANSWERS_MAX = 6;
// How many audiences one request may name (the contract's list ceiling).
const AUDIENCES_MAX = 50;
// How many sources one create may name (the contract's list ceiling).
const SOURCES_PER_CREATE_MAX = 20;
// How many language profiles one new learning context may name per set
// (the contract's list ceiling).
const LANGUAGE_PROFILES_PER_CREATE_MAX = 10;
// {search}: the words one search may hold (the Search page, Cmd+K, the
// content search and the agent's search tool). Longer is refused by name.
const SEARCH_QUERY_MAX = 200;
// {results}: how many results one search answers with, and one page of a
// result list shows; "Show more" pages on, never one huge list.
const SEARCH_RESULTS_MAX = 50;
// QA-22: one chat message's text (what the Author types into the chat).
// Checked on a message sent while the assistant works; refused by name.
const CHAT_MESSAGE_MAX = 20_000;
// QA-28: how many times one chat turn runs, counting its first run and
// every takeover after a restart. A takeover past it closes the turn as
// failed (`turn_attempts_exhausted`) instead of running it again.
const TURN_ATTEMPTS_MAX = 3;
// QA-28: how long a chat turn may stay unfinished, from its opener. A
// takeover past it closes the turn as failed (`turn_time_exhausted`).
const TURN_MINUTES_MAX = 30;
// CL-21 {max-waiting-messages} (proposed): how many messages may wait for
// the assistant at once in one conversation. The assistant reads them all as
// one batch at its next step, so a burst collapses into one read; the next
// message past the ceiling is refused by name until it reads them.
const WAITING_MESSAGES_MAX = 10;
// CL-23 {max-feedback-reports-per-day} (proposed): how many feedback reports
// one person may send per UTC day in one workspace. An honest person can reach
// it (a busy day of reporting), so the next is refused by name
// (`feedback_daily_limit_reached`, carrying the ceiling).
const FEEDBACK_REPORTS_PER_DAY_MAX = 20;
// (proposed, #398) Standing instructions. An honest Author can reach each,
// so each is refused by name (`instruction_text_too_long`,
// `instruction_layer_full`, `instruction_layer_too_long`), carrying the
// ceiling. The character budgets per layer keep the rendered block (every
// layer that applies in one turn) under about 12,000 characters, about 3k
// tokens.
const INSTRUCTION_TEXT_MAX = 500;
const WORKSPACE_INSTRUCTIONS_MAX = 40;
const PROJECT_INSTRUCTIONS_MAX = 20;
const CONVERSATION_INSTRUCTIONS_MAX = 10;
const WORKSPACE_INSTRUCTIONS_CHARS_MAX = 6_000;
const PROJECT_INSTRUCTIONS_CHARS_MAX = 4_000;
const CONVERSATION_INSTRUCTIONS_CHARS_MAX = 2_000;
// (proposed, #398) The assistant's proposals: a one-line reason, at most one
// per turn and three open per conversation (`instruction_proposal_turn_limit`,
// `instruction_proposals_full`). Declined texts are remembered so they are
// not proposed again; the newest DECLINED_INSTRUCTIONS_REMEMBERED are kept.
const INSTRUCTION_PROPOSAL_REASON_MAX = 200;
const INSTRUCTION_PROPOSALS_PER_TURN_MAX = 1;
const INSTRUCTION_PROPOSALS_OPEN_MAX = 3;
const DECLINED_INSTRUCTIONS_REMEMBERED = 200;
// How many parts one plan call may make (plan_parts, plan_course): the
// contract's list ceiling. A course is planned in a few calls, never one
// huge list.
const PLANNED_PARTS_MAX = 50;
// How many parts one structure change may name (put in a new module or
// course, add to one, take out of one): the contract's list ceiling. More
// than a Studio selection holds; only a crafted request reaches it.
const STRUCTURE_PARTS_MAX = 100;
//# sourceMappingURL=limits.js.map